import { normalizeUrlPath } from "./policies";
import {
  getOfflineEntityId,
  getOfflineUserId,
} from "./offlineReferences";

const RESOURCE_LABELS = {
  students: "Élève",
  parentfamilies: "Parent / famille",
  communes: "Commune",
  territories: "Territoire",
  groupments: "Groupement",
  quartiers: "Quartier",
  staffs: "Personnel",
  leaves: "Cong\u00e9",
  presences: "Pr\u00e9sence",
  daystaffs: "Disponibilit\u00e9",
  sections: "Section",
  sectionclasses: "Classe",
  schoolfees: "Frais scolaire",
  suppliers: "Fournisseur",
};

const METHOD_LABELS = {
  post: "Création",
  put: "Modification",
  patch: "Modification",
  delete: "Suppression",
};

const parseData = (data) => {
  if (typeof data !== "string") return data || {};
  try {
    return JSON.parse(data);
  } catch {
    return {};
  }
};

const collectReferences = (value, result) => {
  if (typeof value === "string") {
    const matches = value.match(/offline-(?:user-)?[a-z0-9-]+/gi) || [];
    matches.forEach((reference) => result.add(reference));
  } else if (Array.isArray(value)) {
    value.forEach((item) => collectReferences(item, result));
  } else if (value && typeof value === "object") {
    Object.values(value).forEach((item) => collectReferences(item, result));
  }
};

export const getQueuedRequestReferences = (queued) => {
  const references = new Set();
  collectReferences(parseData(queued?.data), references);
  collectReferences(parseData(queued?.params), references);
  collectReferences(queued?.url, references);
  return [...references];
};

export const getQueueResource = (item) =>
  normalizeUrlPath(item?.url).split("/").filter(Boolean)[0] || "donnée";

const compactName = (...parts) =>
  parts
    .flat()
    .filter((part) => part !== undefined && part !== null && String(part).trim())
    .map((part) => String(part).trim())
    .join(" ");

const asList = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.data)) return value.data;
  return [];
};

const findReference = (references, resource, id) => {
  if (id === undefined || id === null || id === "") return null;
  return (
    asList(references?.[resource]).find(
      (entry) => String(entry?.id) === String(id)
    ) || null
  );
};

const findSchoolfeeCategory = (references, id) => {
  if (id === undefined || id === null || id === "") return null;
  const directMatch = findReference(references, "sectionschoolfeecategories", id);
  if (directMatch) return directMatch;

  return (
    asList(references?.schoolfeecategories)
      .flatMap((category) => asList(category?.sectionschoolfeecategories))
      .find((category) => String(category?.id) === String(id)) || null
  );
};

const getPersonName = (value) => {
  const person =  value?.user || value || {};
  return compactName(
    person.last_name,
    person.family_name,
    person.first_name
  );
};

const getPersonNameStudent = (value) => {
  const person =  value?.user || value || {};
  return compactName(
    person.last_name,
    person.family_name,
    person.first_name
  );
};

const formatPeriod = (start, end) => {
  if (!start && !end) return "";
  if (start && end) return `${start} \u2192 ${end}`;
  return start || end;
};

const getSelectedPeriods = (value, references) => {
  const periods = Array.isArray(value) ? value : value ? [value] : [];
  return periods
    .map((period) => {
      if (period && typeof period === "object") {
        return period.label || period.month || period.name || "";
      }
      const relatedPeriod =
        findReference(references, "months", period) ||
        findReference(references, "trimesters", period);
      return (
        relatedPeriod?.month ||
        relatedPeriod?.name ||
        relatedPeriod?.short_trimester ||
        ""
      );
    })
    .filter(Boolean);
};

export const getQueueItemMetadata = (item, references = {}) => {
  const data = parseData(item?.data);
  const resource = getQueueResource(item);
  const relatedStaff = findReference(references, "staffs", data.staffId);
  const relatedStudent = findReference(references, "students", data.studentId);
  const relatedDay = findReference(references, "days", data.dayId);
  const sectionclassId =
    data.sectionclasseId || relatedStudent?.sectionclasseId;
  const selectedSectionClass =
    findReference(references, "sectionclasses", sectionclassId) ||
    asList(references?.sections)
      .flatMap((section) => asList(section?.sectionclasses))
      .find(
        (sectionclass) =>
          sectionclassId !== undefined &&
          sectionclassId !== null &&
          String(sectionclass?.id) === String(sectionclassId)
      ) ||
    relatedStudent?.sectionclass ||
    relatedStudent?.sectionclasse;
  const schoolfeeCategory = findSchoolfeeCategory(
    references,
    data.sectionschoolfeecategoryId
  );
  const selectedPeriods = getSelectedPeriods(data.monthId, references);
  const selectedClass =
    selectedSectionClass?.class ||
    selectedSectionClass?.classe ||
    findReference(
      references,
      "classes",
      selectedSectionClass?.classId || selectedSectionClass?.classeId
    );
  const className = compactName(
    selectedClass?.short_entitled_class ||
      selectedClass?.short_entitle_class ||
      selectedClass?.title,
    selectedSectionClass?.title,
    selectedSectionClass?.section?.short_entitled_section
  );
  const person = ["students", "staffs"].includes(resource)
    ? data.user || {}
    : data;
  const relatedPersonName =
    getPersonName(relatedStaff) || getPersonNameStudent(relatedStudent);
  const personName = getPersonName(person);
  const details =
    (resource === "suppliers" ? compactName(data.name) : "") ||
    personName ||
    relatedPersonName ||
    compactName(
      data.name,
      data.title,
      data.entitled_section,
      data.short_entitled_section,
      data.category_student,
      data.matricule,
      data.code
    ) ||
    `#${item?.id || "?"}`;
  const businessData = data.student || data.staff || data;
  const context = [
    businessData.matricule ? `Matricule : ${businessData.matricule}` : "",
    resource === "leaves" && data.reason ? `Motif : ${data.reason}` : "",
    resource === "leaves" && (data.date_start || data.date_end)
      ? `P\u00e9riode : ${formatPeriod(data.date_start, data.date_end)}`
      : "",
    resource === "presences" && data.date ? `Date : ${data.date}` : "",
    resource === "presences" && (data.time_start || data.time_end)
      ? `Horaire : ${formatPeriod(data.time_start, data.time_end)}`
      : "",
    resource === "daystaffs"
      ? `Jour : ${relatedDay?.day || relatedDay?.name || data.dayId || "non pr\u00e9cis\u00e9"}`
      : "",
    resource === "daystaffs" && data.actif !== undefined
      ? `\u00c9tat : ${Number(data.actif) ? "disponible" : "indisponible"}`
      : "",
    resource === "schoolfees" && data.pay_mount !== undefined
      ? `Montant payé : ${data.pay_mount}${schoolfeeCategory?.currency ? ` ${schoolfeeCategory.currency}` : ""}#`
      : "",
    resource === "schoolfees"
      ? `Classe : ${className || "non précisée"}`
      : "",
    // resource === "schoolfees"
    //   ? `Frais scolaire : ${compactName(
    //       schoolfeeCategory?.schoolfeecategory?.category,
    //       schoolfeeCategory?.transport?.axe,
    //       schoolfeeCategory?.section?.short_entitled_section
    //     ) || "non précisé"}`
    //   : "",
    resource === "schoolfees"
      ? `${selectedPeriods.length > 1 ? "Echeance" : "Echeance"} : ${selectedPeriods.join(", ") || "non précisé"}`
      : "",
  ].filter(Boolean);

  
  return {
    resource,
    resourceLabel: RESOURCE_LABELS[resource] || resource,
    operationLabel: METHOD_LABELS[item?.method] || item?.method || "Opération",
    details,
    context,
    description: context.join(" \u00b7 "),
    title: `${RESOURCE_LABELS[resource] || resource} · ${details} ${(resource === "schoolfees" && data.pay_mount !== undefined) ? `#Payé : ${data.pay_mount}${schoolfeeCategory?.currency ? ` ${schoolfeeCategory.currency}` : ""}` : ""}`,
  };
};

export const getCreatedReferences = (item) => {
  if (item?.method !== "post") return [];
  const references = [getOfflineEntityId(item)];
  if (getQueueResource(item) === "parentfamilies") {
    references.push(getOfflineUserId(item));
  }
  return references;
};

export const buildQueueDependencyMap = (items) => {
  const ownerByReference = new Map();
  items.forEach((item) => {
    getCreatedReferences(item).forEach((reference) => {
      ownerByReference.set(reference, item.id);
    });
  });

  const dependenciesById = new Map();
  items.forEach((item) => {
    const dependencies = new Set();
    getQueuedRequestReferences(item).forEach((reference) => {
      const ownerId = ownerByReference.get(reference);
      if (ownerId !== undefined && ownerId !== item.id) dependencies.add(ownerId);
    });
    dependenciesById.set(item.id, [...dependencies]);
  });
  return dependenciesById;
};

export const getQueueRemovalImpact = (items, sourceId) => {
  const dependencies = buildQueueDependencyMap(items);
  const ids = new Set([sourceId]);
  const pending = [sourceId];

  while (pending.length) {
    const currentId = pending.shift();
    for (const [id, parents] of dependencies.entries()) {
      if (!ids.has(id) && parents.includes(currentId)) {
        ids.add(id);
        pending.push(id);
      }
    }
  }

  return items.filter((item) => ids.has(item.id));
};
